import Navigation from "@/components/Navigation";
import FooterWithNewsletter from "@/components/ui/footer-column";

const TermsOfUsePage = () => {
  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      <main className="pt-32 pb-16">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="prose prose-lg max-w-none">
            <h1 className="text-4xl font-bold text-foreground mb-8">Terms of Use</h1>

            <div className="space-y-8 text-gray-700 leading-relaxed">
              <section className="space-y-4">
                <p>
                  Welcome to Gimmefy. We built Gimmefy to help individuals and teams create and edit content—like text, images, audio, and video—using powerful tools (including AI) and integrations.
                </p>
                <p>
                  These Terms of Use ("Terms") are meant to be clear, practical, and user-friendly. They apply to self-serve customers and enterprise customers.
                </p>
                <p>
                  By using <a href="https://gimmefy.ai" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">https://gimmefy.ai</a> and related services (together, the "Service"), you agree to these Terms and our Privacy Policy (together, the "Agreements"). If you don't agree, please don't use the Service.
                </p>
                <p>
                  <strong>Core idea:</strong> Gimmefy is a tool/platform. You decide what to create and how to use it. Normal copyright and IP laws apply.
                </p>
                <p>
                  If you have questions, email <a href="mailto:support@gimmefy.ai" className="text-primary hover:underline">support@gimmefy.ai</a>.
                </p>
              </section>
              <section className="space-y-4">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">1. Who we are</h2>
                <p>
                  The Service is operated by Teemuno Pte. Ltd. and its affiliates ("we," "us," "our," "Company").
                </p>
                <p>
                  <strong>Enterprise agreements</strong>
                </p>
                <p>
                  If you have a separate written agreement with us (for example, an Order Form, Master Services Agreement, Enterprise Terms, or a Data Processing Addendum), that agreement controls if it conflicts with these Terms.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">2. Who can use Gimmefy</h2>
                <p>
                  You must be 18 or older to use the Service.
                </p>
                <p>
                  If you use Gimmefy for a company or organization, you confirm you're allowed to accept these Terms for them.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">3. Accounts (individual + organization)</h2>
                <p>
                  <strong>Individual accounts</strong>
                </p>
                <p>
                  You're responsible for:
                </p>
                <ul className="ml-6 list-disc space-y-2">
                  <li>keeping your login details safe;</li>
                  <li>anything that happens under your account;</li>
                  <li>using accurate information and keeping it updated.</li>
                </ul>
                <p>
                  Tell us right away if you think someone else accessed your account.
                </p>
                <p>
                  <strong>Organization accounts (teams / enterprise)</strong>
                </p>
                <p>
                  If your organization uses Gimmefy:
                </p>
                <ul className="ml-6 list-disc space-y-2">
                  <li>the organization may designate one or more admins to manage the account (for example, inviting/removing users, setting permissions, managing billing, viewing usage).</li>
                  <li>you (and your organization) are responsible for your Authorized Users (employees, contractors, and others you allow to use the Service under your organization's account).</li>
                  <li>admins should only grant access to people who are permitted to use the Service and should remove access promptly when it's no longer needed.</li>
                </ul>
                <p>
                  We may suspend or close accounts if needed to protect the Service, comply with law, or enforce these Terms.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">4. Plans, subscriptions, credits, and payments</h2>
                <p>
                  This section applies to both self-serve and enterprise customers. (Enterprise deals may also include an Order Form with additional commercial details.)
                </p>
                <p>
                  <strong>4.1 Subscriptions (self-serve)</strong>
                </p>
                <p>
                  Some features are paid and billed on a subscription basis ("Subscription"), monthly or annually as shown at checkout.
                </p>
                <p>
                  Subscriptions typically renew automatically unless you cancel before renewal. You can cancel in your account settings or by contacting support.
                </p>
                <p>
                  <strong>4.2 Enterprise billing (invoice / Order Form)</strong>
                </p>
                <p>
                  Enterprise customers may be billed by invoice under an Order Form.
                </p>
                <p>
                  Unless the Order Form states otherwise:
                </p>
                <ul className="ml-6 list-disc space-y-2">
                  <li>invoices are due within 30 days of the invoice date;</li>
                  <li>you are responsible for applicable taxes (we'll add them where required, unless you provide valid exemption documentation).</li>
                </ul>
                <p>
                  If payment is overdue, we may pause paid features until the account is brought current.
                </p>
                <p>
                  <strong>4.3 Credits</strong>
                </p>
                <p>
                  Some plans include credits ("Credits") used to run certain tasks.
                </p>
                <p>
                  <strong>Expiry:</strong> Purchased Credits expire 3 months after purchase unless we clearly say otherwise at checkout or in your Order Form.
                </p>
                <p>
                  <strong>How they're used:</strong> Different tasks may use different amounts of Credits.
                </p>
                <p>
                  <strong>4.4 Payments</strong>
                </p>
                <p>
                  When you make a purchase ("Purchase"), you agree that:
                </p>
                <ul className="ml-6 list-disc space-y-2">
                  <li>you're authorized to use the payment method;</li>
                  <li>the information you provide is accurate;</li>
                  <li>you'll pay applicable fees and taxes.</li>
                </ul>
                <p>
                  We use third-party payment processors for self-serve payments.
                </p>
                <p>
                  <strong>4.5 Changes to plans, pricing, and credits</strong>
                </p>
                <p>
                  We may change pricing, plan features, and how Credits work.
                </p>
                <p>
                  If pricing changes for a self-serve Subscription, it will apply from your next billing cycle (or as otherwise shown at purchase).
                </p>
                <p>
                  If you are an enterprise customer, commercial changes are handled through your Order Form/renewal, except for changes needed for security, fairness, legal compliance, or to prevent abuse.
                </p>
                <p>
                  <strong>4.6 Refunds</strong>
                </p>
                <p>
                  All purchases are final and non-refundable, except where a refund is required by law or expressly stated in your Order Form.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">5. Fair use (keeping the Service fast and fair)</h2>
                <p>
                  We want Gimmefy to remain reliable for everyone, so we may apply reasonable usage limits (like rate limits).
                </p>
                <p>
                  Please don't:
                </p>
                <ul className="ml-6 list-disc space-y-2">
                  <li>share personal logins (enterprise accounts should use proper user seats);</li>
                  <li>use bots or automation to mass-run tasks;</li>
                  <li>try to bypass limits.</li>
                </ul>
                <p>
                  If usage is consistently very high, we may recommend a plan that matches your needs or an enterprise/API option (if available).
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">6. What you can't do (common-sense rules)</h2>
                <p>
                  You may not use the Service to:
                </p>
                <ul className="ml-6 list-disc space-y-2">
                  <li>break the law or help others do so;</li>
                  <li>exploit or harm minors;</li>
                  <li>harass, threaten, or abuse others;</li>
                  <li>impersonate people or misrepresent affiliation;</li>
                  <li>upload malware, hack, or disrupt the Service;</li>
                  <li>scrape the Service or access it using automated tools without permission;</li>
                  <li>reverse engineer or attempt to extract our code (except where mandatory law allows it);</li>
                  <li>use Gimmefy or its outputs to train or build competing AI models or datasets (unless we explicitly allow it).</li>
                </ul>
                <p>
                  We may restrict or suspend access if we reasonably believe there's misuse.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">7. Content, copyright, and IP (Gimmefy is a tool)</h2>
                <p>
                  <strong>7.1 Your content</strong>
                </p>
                <p>
                  You may submit prompts, files, and materials ("Input"). The Service may generate results ("Output"). Together, these are "User Content."
                </p>
                <p>
                  You are responsible for:
                </p>
                <ul className="ml-6 list-disc space-y-2">
                  <li>your Input and Output;</li>
                  <li>checking Output before publishing or relying on it;</li>
                  <li>making sure your use complies with laws and third-party rights.</li>
                </ul>
                <p>
                  <strong>7.2 Ownership</strong>
                </p>
                <p>
                  You keep any rights you have in your Input.
                </p>
                <p>
                  We do not claim ownership of your Output as between you and us.
                </p>
                <p>
                  <strong>7.3 Important reality check (global)</strong>
                </p>
                <p>
                  Copyright and IP laws differ by country. In some places, content may need enough human creativity to be copyrightable. Because of that:
                </p>
                <ul className="ml-6 list-disc space-y-2">
                  <li>we don't guarantee that any Output is copyrightable;</li>
                  <li>we don't guarantee that Output is unique or original;</li>
                  <li>we don't guarantee that Output won't infringe someone else's rights.</li>
                </ul>
                <p>
                  If you plan to use Output commercially (ads, brands, packaging, published media, etc.), you should do the checks you'd normally do—like rights clearance, trademark checks, and plagiarism review.
                </p>
                <p>
                  <strong>7.4 Similar outputs</strong>
                </p>
                <p>
                  AI systems can sometimes produce similar results for different users. That's normal, and it's another reason to review content before using it.
                </p>
                <p>
                  <strong>7.5 Permission to run the Service</strong>
                </p>
                <p>
                  To provide the Service, you give us permission to host, process, and use your User Content only as needed to:
                </p>
                <ul className="ml-6 list-disc space-y-2">
                  <li>run and maintain the Service;</li>
                  <li>generate Outputs;</li>
                  <li>keep the Service secure, prevent abuse, and troubleshoot;</li>
                  <li>comply with legal obligations.</li>
                </ul>
                <p>
                  We don't use this permission to claim your work as ours.
                </p>
                <p>
                  <strong>7.6 Improving the Service</strong>
                </p>
                <p>
                  We may use service usage data and, where described in our Privacy Policy, some content to improve the Service (including with AI tools).
                </p>
                <p>
                  If you are an enterprise customer and your contract provides different settings (for example, opt-out from certain uses), your contract controls.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">8. Confidentiality (mainly for enterprise)</h2>
                <p>
                  We sometimes share non-public information with each other—for example, security documentation, product roadmaps, pricing, or internal workflows ("Confidential Information").
                </p>
                <p>
                  If you receive Confidential Information from us, you agree to:
                </p>
                <ul className="ml-6 list-disc space-y-2">
                  <li>use it only to evaluate or use the Service;</li>
                  <li>protect it using reasonable care; and</li>
                  <li>not share it with third parties except your employees/contractors who need it and who are bound by confidentiality obligations.</li>
                </ul>
                <p>
                  This section doesn't apply to information that is public, already known to you without confidentiality restrictions, independently developed by you, or lawfully received from a third party.
                </p>
                <p>
                  If your enterprise agreement includes a separate confidentiality clause, that clause controls.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">9. Our platform and brand</h2>
                <p>
                  The Service (software, design, templates, logos, and other non-user content) is owned by us or our licensors.
                </p>
                <p>
                  You're welcome to talk about Gimmefy and link to our site in a normal way, but please don't imply we endorse you, and don't copy or resell our Service or branding.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">10. Copyright complaints</h2>
                <p>
                  If you believe content on the Service infringes your copyright, email <a href="mailto:support@gimmefy.ai?subject=Copyright Infringement" className="text-primary hover:underline">support@gimmefy.ai</a> with the subject "Copyright Infringement" and include:
                </p>
                <ul className="ml-6 list-disc space-y-2">
                  <li>what you believe was infringed;</li>
                  <li>where it appears on the Service (link or clear description);</li>
                  <li>your contact details;</li>
                  <li>a short statement that you believe the use is not authorized.</li>
                </ul>
                <p>
                  We'll review and take appropriate action.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">11. Third-party services and integrations</h2>
                <p>
                  The Service may connect to third-party tools and services ("Third-Party Services"). Those services have their own terms and policies.
                </p>
                <p>
                  We don't control Third-Party Services and aren't responsible for their content, availability, or actions.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">12. Support and service availability</h2>
                <p>
                  We work hard to keep Gimmefy available, but downtime can happen (maintenance, upgrades, outages).
                </p>
                <p>
                  Support channels and response times may differ by plan. If you have an enterprise support package or SLA in an Order Form, that document controls.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">13. No guarantees</h2>
                <p>
                  We provide the Service and Outputs as is and as available.
                </p>
                <p>
                  That means:
                </p>
                <ul className="ml-6 list-disc space-y-2">
                  <li>we don't promise the Service will always be available, error-free, or perfectly accurate;</li>
                  <li>Outputs can be wrong or incomplete;</li>
                  <li>you should not rely on Outputs as professional advice (legal, medical, financial, etc.) without qualified review.</li>
                </ul>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">14. Liability limits (keeping this fair)</h2>
                <p>
                  To the maximum extent permitted by law:
                </p>
                <ul className="ml-6 list-disc space-y-2">
                  <li>we aren't responsible for indirect or unexpected losses (like lost profits, lost data, business interruption);</li>
                  <li>if we are found liable, our total liability for claims related to the Service will not exceed the amount you paid us for the Service in the 12 months before the issue arose.</li>
                </ul>
                <p>
                  Some countries don't allow certain limits, so parts of this section may not apply to you.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">15. Ending access</h2>
                <p>
                  You can stop using the Service at any time.
                </p>
                <p>
                  We may suspend or terminate access if you seriously or repeatedly violate these Terms, if we need to protect the Service or other users, or if required by law.
                </p>
                <p>
                  If you are an enterprise customer, your contract may include additional offboarding steps (like admin export tools) and those will apply.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">16. Changes to these Terms</h2>
                <p>
                  We may update these Terms from time to time. We'll post the latest version on our site.
                </p>
                <p>
                  If you keep using the Service after changes take effect, that means you accept the updated Terms.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">17. Governing law</h2>
                <p>
                  Unless mandatory local consumer protection laws say otherwise, these Terms are governed by the laws of Singapore, and disputes will be handled in Singapore courts.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">18. Contact</h2>
                <p>
                  Email: <a href="mailto:support@gimmefy.ai" className="text-primary hover:underline">support@gimmefy.ai</a>
                </p>
                <p>
                  Contact page: <a href="https://gimmefy.ai/contact" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">https://gimmefy.ai/contact</a>
                </p>
              </section>

              <section className="space-y-4 mt-12 pt-8 border-t border-border">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">Acceptable Use Policy</h2>
                <p>
                  We want Gimmefy to be useful, safe, and respectful. Please:
                </p>
                <ul className="ml-6 list-disc space-y-2">
                  <li><strong>Be lawful.</strong> Don't use Gimmefy for illegal activity.</li>
                  <li><strong>Be respectful.</strong> No harassment, exploitation, or harm—especially involving minors.</li>
                  <li><strong>Respect rights.</strong> Only upload what you have rights to use. Don't use outputs to copy others' copyrighted or trademarked work.</li>
                  <li><strong>Don't attack the Service.</strong> No hacking, malware, scraping, or bypassing limits.</li>
                  <li><strong>Use the right access.</strong> Use your own account or your organization's assigned seat—don't share logins.</li>
                  <li><strong>Review before you publish.</strong> Treat outputs like drafts—double-check facts and rights.</li>
                </ul>
                <p>
                  If you're unsure, email <a href="mailto:support@gimmefy.ai" className="text-primary hover:underline">support@gimmefy.ai</a>. We'll try to help, and we may restrict accounts that break these rules.
                </p>
              </section>

              <p className="text-sm text-muted-foreground/70 mt-12 pt-8 border-t border-border">
                These Terms were last updated December 2025.
              </p>
            </div>
          </div>
        </div>
      </main>

      <FooterWithNewsletter />
    </div>
  );
};

export default TermsOfUsePage;
